import React, { useEffect } from 'react';
import { api } from 'utils/api-client';
import { useModelState } from '@rexlabs/model-generator';
import sessionModel from 'data/models/custom/session';
import { getAccountRegion } from 'shared/utils/region';
import config from 'src/config';
import { Bugsnag } from 'src/lib/bugsnag';

interface ZendeskChatProps {
  apiKey: string;
}

declare global {
  interface Window {
    zE: (
      command: string,
      commandParams: string,
      callback?: ((cb: (...args: any[]) => void) => void) | string[]
    ) => void;
  }
}

// The Zendesk widget is a third-party script we don't control. When Zendesk
// changes or migrates the messaging widget, unsupported commands throw
// "Method <x> does not exist". This component renders globally, so an unguarded
// throw in the effects below propagates to the app-level error boundary and
// white-screens every page. Route every zE call through this guard so a widget
// change degrades to "chat doesn't update" instead of taking down the app.
function safeZE(...args: Parameters<Window['zE']>): void {
  if (!window.zE) {
    return;
  }
  try {
    window.zE(...args);
  } catch (e) {
    // Report but never rethrow: this renders globally and a throw here white-screens the app.
    Bugsnag.leaveBreadcrumb('Zendesk messenger command failed', {
      command: args[0],
      param: String(args[1]),
      error: e instanceof Error ? e.message : String(e)
    });
  }
}

export const ZendeskMessenger: React.FC<ZendeskChatProps> = ({ apiKey }) => {
  const session = useModelState(sessionModel);

  useEffect(() => {
    if (config.MODE === 'development') {
      return;
    }

    if (!session.user || !session.ready || !session.currentAccountId) {
      safeZE('messenger', 'hide');
      safeZE('messenger', 'logoutUser');
      return;
    } else {
      const countryCode = getAccountRegion(session);
      safeZE(
        'messenger:set',
        'conversationTags',
        countryCode === 'GB' ? ['region:uk'] : ['region:apac']
      );
      safeZE('messenger', 'show');
    }
    api
      .get('/zendesk/auth')
      .then(({ data }) => {
        safeZE('messenger', 'loginUser', function (callback) {
          callback(data.token);
        });
      })
      .catch(() => {
        // Auth failure must not crash the app; chat just stays logged out.
      });
  }, [session.user, session.ready, window.zE, session.currentAccountId]);

  useEffect(() => {
    if (config.MODE === 'development') {
      return;
    }

    const existingScript = document.getElementById('ze-snippet');
    if (existingScript) return;
    const script = document.createElement('script');
    script.id = 'ze-snippet';
    script.src = `https://static.zdassets.com/ekr/snippet.js?key=${apiKey}`;
    document.body.appendChild(script);

    return () => {
      const existingScript = document.getElementById('ze-snippet');
      if (existingScript) document.body.removeChild(existingScript);
    };
  }, [apiKey]);

  return null;
};
